/**
 * Store de autenticación con Pinia.
 *
 * Maneja la sesión actual y las acciones de login/logout/forgot/reset.
 * La sucursal activa pertenece exclusivamente a `sucursalStore`.
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  AnySession,
  LoginResponse,
  ManagerSession,
  SucursalResumen,
} from '@carwash-pos/shared';
import { api, ApiException } from '@/lib/api';
import { useSucursalStore } from '@/stores/sucursal';
import { useCartStore } from '@/stores/cart';
import { useCatalogStore } from '@/stores/catalog';

export type ModoAdmin = 'admin' | 'manager';
const ADMIN_MODE_STORAGE_KEY = 'cwpos_admin_mode';

export const useAuthStore = defineStore('auth', () => {
  // Estado reactivo
  const user = ref<AnySession | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const modoAdmin = ref<ModoAdmin | null>(
    (() => {
      const stored = sessionStorage.getItem(ADMIN_MODE_STORAGE_KEY);
      return stored === 'admin' || stored === 'manager' ? stored : null;
    })(),
  );
  // Getters
  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.role === 'admin');
  const isManager = computed(() => user.value?.role === 'manager');
  const sucursalesAsignadas = computed<SucursalResumen[]>(() => {
    if (user.value?.role === 'manager') {
      return (user.value as ManagerSession).sucursales;
    }
    return [];
  });

  function limpiarSesionLocal(): void {
    user.value = null;
    modoAdmin.value = null;
    sessionStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
    useSucursalStore().reset();
    useCartStore().resetear();
    useCatalogStore().reset();
  }

  async function cargarSucursalesAdmin(): Promise<void> {
    const res = await api.get<{ sucursales: SucursalResumen[] }>('/sucursales');
    useSucursalStore().setDisponibles(res.sucursales);
  }

  // Acciones
  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await api.post<LoginResponse>('/auth/login', { email, password });
      user.value = res.user;
      modoAdmin.value = null;
      sessionStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      useSucursalStore().reset();
      useCartStore().resetear();
      useCatalogStore().reset();
      if (!res.user.mustChangePassword) {
        await useSucursalStore().recuperarOperacion();
        if (res.user.role === 'admin') await cargarSucursalesAdmin().catch(() => undefined);
      }
    } catch (err) {
      if (err instanceof ApiException && err.status === 401) {
        error.value = 'Email o contraseña incorrectos.';
      } else if (err instanceof ApiException && err.status === 403) {
        error.value = 'Tu cuenta está inactiva. Contacta al administrador.';
      } else {
        error.value = 'No se pudo iniciar sesión. Intenta de nuevo.';
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignorar errores: igual limpiamos el estado local.
    }
    limpiarSesionLocal();
  }

  async function refresh(): Promise<void> {
    try {
      const res = await api.get<{ user: AnySession | null }>('/auth/me');
      user.value = res.user;
      if (res.user?.role !== 'admin') {
        modoAdmin.value = null;
        sessionStorage.removeItem(ADMIN_MODE_STORAGE_KEY);
      }
      if (res.user?.mustChangePassword) {
        useSucursalStore().reset();
        return;
      }
      await useSucursalStore().recuperarOperacion();
      if (res.user?.role === 'admin') await cargarSucursalesAdmin().catch(() => undefined);
      if (res.user?.role === 'manager') {
        const manager = res.user as ManagerSession;
        const sucursalStore = useSucursalStore();
        if (
          sucursalStore.activeSucursalId !== null &&
          !manager.sucursales.some((sucursal) => sucursal.id === sucursalStore.activeSucursalId)
        ) {
          sucursalStore.clear();
        }
      }
    } catch {
      limpiarSesionLocal();
    }
  }

  function setModoAdmin(modo: ModoAdmin): void {
    modoAdmin.value = modo;
    sessionStorage.setItem(ADMIN_MODE_STORAGE_KEY, modo);
  }

  function invalidarSesion(): void {
    limpiarSesionLocal();
  }

  async function forgotPassword(email: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await api.post('/auth/forgot-password', { email });
    } catch {
      error.value = 'No se pudo enviar el email. Intenta de nuevo.';
      throw new Error('forgot_failed');
    } finally {
      loading.value = false;
    }
  }

  async function resetPassword(token: string, newPassword: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await api.post('/auth/reset-password', { token, new_password: newPassword });
    } catch (err) {
      if (err instanceof ApiException && err.status === 400) {
        error.value = 'El link expiró o ya fue usado. Solicita uno nuevo.';
      } else {
        error.value = 'No se pudo restablecer la contraseña.';
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (user.value) user.value = { ...user.value, mustChangePassword: false };
    } catch (err) {
      if (err instanceof ApiException && err.error === 'invalid_current_password') {
        error.value = 'La contraseña temporal no es correcta.';
      } else if (err instanceof ApiException && err.error === 'password_unchanged') {
        error.value = 'Elige una contraseña distinta a la temporal.';
      } else {
        error.value = 'No se pudo cambiar la contraseña. Intenta de nuevo.';
      }
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    user,
    loading,
    error,
    modoAdmin,
    isAuthenticated,
    isAdmin,
    isManager,
    sucursalesAsignadas,
    login,
    logout,
    refresh,
    forgotPassword,
    resetPassword,
    changePassword,
    setModoAdmin,
    invalidarSesion,
  };
});
